// =============================================================================
// Decay calculation - the printable sheet
// =============================================================================
//
// Kept out of DecayCalculator.js because it is the part most likely to be
// adjusted again, and that file is 32 KB of form.
//
// Laid out as a document rather than as a screen: white, left aligned, black
// text, one rule under the header and one above the result. That is the same
// decision the weekly digest email was rebuilt on, and it holds harder on
// paper - browsers drop background colours when printing unless told not to,
// so a design that leans on coloured panels prints as a set of empty boxes.
// Nothing here depends on a background being filled.
//
// Every label comes from a key the calculator already uses. A printed sheet is
// not the place to introduce a string that exists in only one language. That
// rule is also why the nuclide sits in the header without a label: it is a
// value that names itself, so it needed no new string to be promoted there.
// =============================================================================

// The company mark, the same paths as components/AmmoLogo.js, written as HTML
// rather than JSX. It is drawn content, not a background, so it survives
// printing where a coloured header block would not.
const LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 110.35 66.09" class="mark">
  <defs>
    <linearGradient id="ammo-mark" x1="-4.84" y1="37.68" x2="71.5" y2="33.37" gradientUnits="userSpaceOnUse">
      <stop offset="0" stop-color="aqua"/>
      <stop offset="1" stop-color="#339"/>
    </linearGradient>
  </defs>
  <path fill="url(#ammo-mark)" d="M67,33A10.34,10.34,0,1,0,77.33,22.7,10.35,10.35,0,0,0,67,33Z"/>
  <path fill="url(#ammo-mark)" d="M66.24,1.91v0l-.12.05c-1.09.4-22,10.25-40,18.75l21.17,10C53.8,27.59,65,22.32,69.85,20a15,15,0,1,1,7.46,28,14.79,14.79,0,0,1-7.19-1.86l-2.28-1C65.35,44.1,39.71,32,21,23.14,9.31,28.63,0,33,0,33s64.18,30.4,66.13,31.11l.11,0v0a33,33,0,1,0,0-62.27Z"/>
</svg>`;

const STYLES = `
  @page { size: A4; margin: 18mm; }

  * { margin: 0; padding: 0; box-sizing: border-box; }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #111827;
    font-size: 11pt;
    line-height: 1.45;
    padding: 28px;
    background: #fff;
    /* The mark is a gradient fill. Chrome and Safari print in an "economy"
       mode by default that drops it, which left the sheet headed by an empty
       square. This asks for the drawn content to be printed as drawn; it does
       not reintroduce any background, since there are none to reintroduce. */
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Roughly the printable width of A4 inside the margins above, so what the
     window shows before printing is what comes out of the printer. */
  .sheet { max-width: 170mm; margin: 0 auto; }

  header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 10px;
    margin-bottom: 20px;
    border-bottom: 1px solid #111827;
  }
  .mark { width: 34px; height: auto; flex: none; }
  .title { font-size: 13pt; font-weight: 700; letter-spacing: -0.01em; }
  .subtitle { font-size: 9.5pt; color: #6b7280; }

  /* The nuclide, opposite the mark. It and the final activity are the two
     facts somebody reaches for on a filed sheet; as a row in the middle of
     the parameters the first of them was indistinguishable from the half-life
     beside it. */
  .subject { margin-left: auto; text-align: right; padding-left: 12px; }
  .nuclide { font-size: 15pt; font-weight: 700; letter-spacing: -0.01em; }
  .element { font-size: 9.5pt; color: #6b7280; }

  /* A label column of fixed width rather than a two-across grid: on paper,
     values that start at the same place can be scanned down in one movement,
     and a long isotope name no longer drags an unrelated field with it. */
  table { width: 100%; border-collapse: collapse; }
  th {
    width: 52mm;
    text-align: left;
    font-weight: 400;
    font-size: 9.5pt;
    color: #6b7280;
    padding: 5px 12px 5px 0;
    vertical-align: baseline;
  }
  td {
    font-weight: 500;
    padding: 5px 0;
    vertical-align: baseline;
    font-variant-numeric: tabular-nums;
  }
  /* Was #f1f5f9, which is a rule you can see on a screen and cannot see on
     paper: at 600 dpi it dithers away to nothing on most office printers. */
  tr + tr th, tr + tr td { border-top: 1px solid #e5e7eb; }

  .result {
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid #111827;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .result .label {
    font-size: 9pt;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.07em;
  }
  .result .value {
    font-size: 22pt;
    font-weight: 700;
    margin-top: 2px;
    font-variant-numeric: tabular-nums;
  }
  .result .unit { font-size: 13pt; font-weight: 500; color: #374151; }
  /* Darker than it was: this is the statement of what was computed, not a
     watermark, and at #9ca3af it was the faintest thing on the page. */
  .formula { margin-top: 8px; font-size: 9pt; color: #6b7280; }

  footer {
    margin-top: 24px;
    padding-top: 8px;
    border-top: 1px solid #e5e7eb;
    font-size: 8.5pt;
    color: #9ca3af;
  }

  @media print { body { padding: 0; } }
`;

/** Escape for the printable page.
 *
 *  Element and isotope names are typed by an administrator and land in a
 *  document assembled as markup, so a name containing an angle bracket would
 *  otherwise become one.
 */
function escapeHtml(value) {
  return String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Open a printable sheet for one decay result.
 *
 * Takes both shapes the calculator produces: the dated one carries origin,
 * target and elapsed time, the quick one carries a single interval instead.
 * Rows with nothing in them are left out rather than printed empty.
 *
 * Returns false when the browser refused to open the window, which is the one
 * failure the caller has to say something about.
 */
export function printDecayReport({ result, t, locale }) {
  if (!result) return false;

  // Nuclide and element are in the header instead, so they are not repeated
  // here. Whichever of the two is missing simply does not appear, in the
  // header as in the table before it.
  const rows = [
    [t("decay.halfLife"), result.halfLife],
    [t("decay.initialActivity"), result.initialActivity],
    [t("decay.originDateTime"), result.originDt],
    [t("decay.targetDateTime"), result.targetDt],
    [t("decay.elapsedTime"), result.elapsed],
    [t("decay.quickTimeDelta"), result.timeDelta],
    [t("decay.decayFactor"), result.decayFactor],
  ].filter(([, value]) => value);

  const rowsHtml = rows
    .map(([label, value]) => `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`)
    .join("\n      ");

  const nuclideHtml = result.isotope
    ? `<div class="nuclide">${escapeHtml(result.isotope)}</div>`
    : "";
  const elementHtml = result.element
    ? `<div class="element">${escapeHtml(result.element)}</div>`
    : "";
  const subjectHtml = nuclideHtml || elementHtml
    ? `<div class="subject">${nuclideHtml}${elementHtml}</div>`
    : "";

  const printedAt = new Date();

  // The window title is what a browser offers as the filename when the sheet
  // is saved as a PDF, so it names the nuclide and the day rather than being
  // the same six words on every file in the folder.
  const docTitle = `AMMO Core - ${result.isotope || ""} - ${printedAt.toISOString().slice(0, 10)}`;

  const title = escapeHtml(t("decay.title"));
  const finalLabel = escapeHtml(t("decay.finalActivity"));

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${escapeHtml(docTitle)}</title>
<style>${STYLES}</style></head>
<body>
  <div class="sheet">
    <header>
      ${LOGO_SVG}
      <div>
        <div class="title">AMMO Core</div>
        <div class="subtitle">${title}</div>
      </div>
      ${subjectHtml}
    </header>

    <table>
      ${rowsHtml}
    </table>

    <div class="result">
      <div class="label">${finalLabel}</div>
      <div class="value">${escapeHtml(result.finalActivity)} <span class="unit">${escapeHtml(result.finalUnit)}</span></div>
      <div class="formula">A = A<sub>0</sub> &middot; 2<sup>&minus;t / t&frac12;</sup></div>
    </div>

    <footer>AMMO Core &middot; ${escapeHtml(printedAt.toLocaleString(locale))}</footer>
  </div>
</body></html>`;

  // A blocked pop-up returns null, and reaching for its document threw, which
  // left the button looking as though it had simply done nothing.
  const printWindow = window.open("", "_blank", "width=860,height=960");
  if (!printWindow) return false;

  printWindow.document.write(html);
  printWindow.document.close();

  // The window opens on about:blank, which has already fired its load event,
  // and whether writing a new document into it fires another one is not the
  // same in every browser. Both paths are wired up and the flag decides which
  // one counts: the sheet has no image or web font to wait for, so whichever
  // arrives first is late enough.
  let started = false;
  const startPrint = () => {
    if (started) return;
    started = true;
    printWindow.focus();
    printWindow.print();
  };
  printWindow.onload = startPrint;
  setTimeout(startPrint, 250);

  return true;
}
